// Version: 2.30
// v2.30: Microsoft 365 tab gains Client backups (CV-58, app.py v4.53): on or off,
//        nightly time, nightly and weekly copies kept, Back up all now, the last
//        run and each client's last backup (failures first, no team marked).
// v2.29: New Microsoft 365 tab (CV-57, app.py v4.51): team owners and members for client
//        teams, team name format and channel name, platform backup site, and Test
//        connection (certificate sign-in, each person found, teams readable, backup site).
// v2.28: Inputs and selects take their look from index.css (input-sm for compact) (CV-50).
// v2.27: compose.yml row has a status dot like the other Versions rows: green when applied, amber naming services to apply with docker compose up -d, grey when it can't be checked.
// v2.26: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; no inline radius, padding or font size.
// v2.25: Edge scripts row shows whether every edge node runs the scripts in
//        edge-dist (app.py v4.15), with each node that's behind linking to its
//        Scripts tab on Edge Nodes. It looked for a git pull that was never
//        built and always said "Not found".
// v2.24: The automatic email setting says it is for External Cyber Security
//        Assessments only, and where the other two reports' sending is set.
// v2.23: Tab bar no longer shows a scroll bar on desktop (the active tab's
//        underline overhangs the border by 1px, which made it scroll vertically).
// v2.22: Template preview fills {grade} and {report_date} (the new External
//        Cyber Security Assessment template, app.py v4.09).
// v2.21: Health Alerts works on a phone (the two columns stack, tabs scroll,
//        buttons wrap) and Recent Alerts says what each alert was about: tap
//        one for its message, facts (client, site, network, devices) and
//        details, and Open goes to the client's devices. ?tab=alerts opens
//        the tab (the dashboard's Alerts item). Email Templates stacks too.
// v2.20: Alert Triggers gains "Critical vulnerabilities on client devices"
//        (alert_network_cve) for new CRITICAL CVEs found by edge network scans.
// v2.19: Email template preview shows the real header tagline and footer line
//        (EMAIL_HEADER_TAGLINE / EMAIL_FOOTER_LINE via /api/admin/email-branding)
//        instead of fixed text.
// v2.18: Email Templates tab gains an Automatic Report Emails toggle
//        (assessment auto-email to clients, off by default) backed by
//        /api/admin/assessment-config.
// 2.17: Email Templates preview supports the new Prospect Outreach (Sales)
//       template from app.py v3.58: {contact_name} and {sender_name} have
//       sample values, and the preview shows the unsubscribe line that is
//       added under the sign-off on every outreach email (not editable).
// 2.16: Edge Fleet card rows use their own stacked layout: status dot, node
//       name and short ID on one line, issues and information full width
//       underneath. The shared key-value row kept the label at full width and
//       squeezed the value column to one character, so long node names pushed
//       the short ID and issue text into a vertical strip. Shows DSM version and
//       container image updates (information only) from the node check.
// 2.15: System Admin phase 3 (app.py v3.48+).
//       - New Jobs tab: every background scheduler in the API, grouped, with
//         schedule, last triggered and last looped times. Red when a thread has
//         died or stopped looping, amber when a scheduled job missed its window.
//         Tab label carries a count of jobs needing attention.
//       - Edge Fleet card on the System tab: one row per node with online state
//         and anything needing attention (heartbeat health, scripts behind the
//         edge-dist manifest, node check status and age).
//       - Maintenance card expanded: dangling images with Prune, Redis memory and
//         last save, storage used by clients, analytics, reports and ticket
//         attachments with Recalculate, watchdog log size with Trim.
// 2.14: Restart Core Services moved into the page header, right-aligned opposite
//       the title, with the Refresh, Deploy and Run groups on their own row
//       underneath. The toolbar was one row too wide at typical desktop widths,
//       so the restart button always wrapped onto a line of its own.
// 2.13: System Admin phase 2 (app.py v3.47+).
//       - New Integrations tab: .env versus compose.yml versus running
//         container audit (set but not in container, changed since the container
//         was created, needed but missing, unused), filterable to rows that need
//         attention. Live Test button for supported keys with the cached result
//         and its age. Mail card with Email Poller last activity and Mail Relay
//         last delivery and queue depth.
//       - Versions card on the System tab: running API against the NAS copy,
//         built frontend bundle against the one nginx serves, compose.yml,
//         edge-dist commit and last pull, watchdog last run and log size.
// 2.12: System tab rework (app.py v3.46+).
//       - Services list comes entirely from the API (Docker discovery plus
//         compose.yml) — CONTAINER_LABELS removed. Rows are grouped (Platform,
//         Tunnels, Integrations and Pollers, TrustM365, Clearaware, Network,
//         Other) with a running count per group and an overall summary line.
//       - Status dot uses the API rag value; the row note explains amber/red
//         (restarts, exit code, failing health check, not deployed).
//       - Restart asks for confirmation where the API supplies a warning
//         (Portal Tunnel). Not-deployed services show Start instead of Restart.
//       - Restart All renamed Restart Core Services; the confirm dialog lists
//         the exact services. Progress panel shows per-service results, with
//         a faster refresh while it runs.
//       - All timestamps en-GB with Europe/London time and a GMT suffix.
//       - Header buttons grouped: Deploy, Run, then Restart Core Services.
// 2.11: Email Templates tab — added "Make Default" button (confirm dialog
//       then promotes the currently active content to baseline via
//       POST .../make-default, clearing the "customised" dot instead of
//       leaving it stuck forever like Reset would). Preview panel extended
//       with placeholder substitutions and structured blocks for the 4 new
//       ticket_* templates (app.py v2.78+) — template pill list itself is
//       already data-driven from the GET response, no changes needed there.
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { authFetch, getSseToken } from './auth';
import './Admin.css';

// en-GB, Europe/London, GMT suffix — platform-wide date/time convention.
// Timestamps without a zone suffix are UTC (datetime.utcnow().isoformat()).
function fmtTime(iso) {
  if (!iso) return '';
  const str = /(Z|[+-]\d\d:?\d\d)$/i.test(iso) ? iso : `${iso}Z`;
  const d = new Date(str);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London',
  }) + ' GMT';
}

const RAG_COLOUR = { green: 'var(--green)', amber: 'var(--amber)', red: 'var(--red)', grey: 'var(--text-muted)' };

function StatusDot({ rag }) {
  const color = RAG_COLOUR[rag] || 'var(--red)';
  return <span style={{width:8,height:8,borderRadius:'50%',background:color,display:'inline-block',flexShrink:0,
    boxShadow: rag === 'green' ? `0 0 6px ${color}` : 'none'}} />;
}

function ServiceRow({ c, logs, acting, onFetchLogs, onRestartContainer }) {
  const notDeployed = c.status === 'not deployed';
  const restarts = Number(c.restart_count ?? c.restarts ?? 0);
  const meta = [
    c.status,
    c.health ? `health ${c.health}` : '',
    c.started ? `started ${fmtTime(c.started)}` : '',
    restarts > 0 ? `${restarts} restart${restarts === 1 ? '' : 's'}` : '',
  ].filter(Boolean).join(' · ');
  return (
    <div className="admin-service-row">
      <div className="admin-service-main">
        <StatusDot rag={c.rag} />
        <div style={{flex:1,minWidth:0}}>
          <div className="admin-service-label">{c.label}</div>
          <div className="admin-service-meta">{meta}</div>
          {c.note && c.rag !== 'green' && (
            <div className="admin-service-note" style={{color: RAG_COLOUR[c.rag] || 'var(--text-muted)'}}>{c.note}</div>
          )}
        </div>
        <div className="admin-service-actions">
          {!notDeployed && (
            <button className="btn btn-secondary btn-sm" onClick={() => onFetchLogs(c.name)}>
              {logs[c.name] ? 'Hide logs' : 'Logs'}
            </button>
          )}
          {c.restart_allowed && (
            <button className="btn btn-secondary btn-sm"
              disabled={acting[c.name]} onClick={() => onRestartContainer(c)}>
              {acting[c.name] ? <span className="spinner" /> : notDeployed ? 'Start' : 'Restart'}
            </button>
          )}
        </div>
      </div>
      {logs[c.name] && <pre className="admin-log-box">{logs[c.name]}</pre>}
    </div>
  );
}

function RestartAllPanel({ status }) {
  if (!status) return null;
  const results = status.results || [];
  const failed  = results.filter(r => !r.ok);
  const title = status.running
    ? `Restarting core services — ${results.length} of ${status.total} done`
    : failed.length
      ? `Restart Core Services finished with ${failed.length} failure${failed.length === 1 ? '' : 's'}`
      : `Restart Core Services finished — ${results.length} of ${status.total} restarted`;
  return (
    <div className="card admin-card" style={{marginBottom:16}}>
      <div className="admin-card-title" style={{marginBottom:6}}>
        {status.running && <span className="pulse-dot" style={{display:'inline-block',marginRight:6}} />}
        {title}
      </div>
      <div className="admin-service-meta" style={{marginBottom:10}}>
        Started {fmtTime(status.started)}{status.by ? ` by ${status.by}` : ''}
        {status.finished ? ` · finished ${fmtTime(status.finished)}` : ''}
      </div>
      <div className="admin-restart-results">
        {results.map(r => (
          <div key={r.name} className="admin-restart-result">
            <span style={{color: r.ok ? 'var(--green)' : 'var(--red)', fontWeight:700}}>{r.ok ? '✓' : '✗'}</span>
            <span>{r.label}</span>
            {!r.ok && r.message && <span className="admin-service-note" style={{color:'var(--red)'}}>{r.message}</span>}
          </div>
        ))}
      </div>
      {!status.running && results.some(r => r.label === 'Portal API' && r.ok) && (
        <div className="admin-service-meta" style={{marginTop:10}}>
          Portal API is recreated by the watchdog within 60 seconds of the restart being queued.
        </div>
      )}
    </div>
  );
}

function Toggle({ checked, onChange, label, sublabel }) {
  return (
    <label style={{display:'flex',alignItems:'flex-start',gap:12,cursor:'pointer',padding:'10px 0',borderBottom:'1px solid var(--border)',textTransform:'none',letterSpacing:'normal',fontSize:'inherit',fontWeight:'inherit',color:'inherit',marginBottom:0}}>
      <div style={{position:'relative',width:40,height:22,flexShrink:0,marginTop:2}}>
        <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
          style={{opacity:0,width:0,height:0,position:'absolute'}} />
        <div style={{
          position:'absolute',inset:0,borderRadius:11,transition:'background 0.2s',
          background: checked ? 'var(--accent)' : 'var(--border)',
        }} />
        <div style={{
          position:'absolute',top:2,left: checked ? 20 : 2,width:18,height:18,
          borderRadius:'50%',background:'#fff',transition:'left 0.2s',boxShadow:'0 1px 3px rgba(0,0,0,0.3)',
        }} />
      </div>
      <div>
        <div style={{fontSize:13,fontWeight:600,color:'var(--text)'}}>{label}</div>
        {sublabel && <div style={{fontSize:11,color:'var(--text-muted)',marginTop:2,fontWeight:400}}>{sublabel}</div>}
      </div>
    </label>
  );
}

// ── System tab (existing content) ────────────────────────────────────────────

function SystemTab({ data, loading, logs, acting, messages, failedScans, buildLog, building, buildDone,
  apiBuildLog, apiBuildDone, buildingApi, buildLogRef, apiBuildLogRef,
  onFetchLogs, onRestartContainer, onRestartAll, onClearLogs, onClearFailedScan,
  onRebuildApi, onRebuildFrontend, onSyncFrontend, versions,
  fleet, maint, onPrune, onTrim, onRecalc }) {

  if (loading) return <div style={{padding:48,textAlign:'center'}}><span className="spinner" /></div>;

  return (
    <>
      <RestartAllPanel status={data?.restart_all} />
      <div className="admin-grid">
        {/* Services — grouped, discovered by the API from Docker and compose.yml */}
        <div className="card admin-card">
          <div className="admin-services-head">
            <div className="admin-card-title" style={{marginBottom:0}}>Services</div>
            {data?.generated && <div className="admin-service-meta">Updated {fmtTime(data.generated)}</div>}
          </div>
          {data?.summary && (
            <div className="admin-services-summary">
              {data.summary.total} services · {data.summary.running} running
              {data.summary.red > 0 && <span style={{color:'var(--red)'}}> · {data.summary.red} need attention</span>}
              {data.summary.amber > 0 && <span style={{color:'var(--amber)'}}> · {data.summary.amber} warning{data.summary.amber === 1 ? '' : 's'}</span>}
            </div>
          )}
          {(data?.groups || []).map(g => {
            const rows = (data?.containers || []).filter(c => c.group === g.id);
            if (!rows.length) return null;
            const up = rows.filter(c => c.status === 'running').length;
            return (
              <div key={g.id} className="admin-service-group">
                <div className="admin-service-group-head">
                  <span>{g.label}</span>
                  <span className="admin-service-group-count">{up} of {rows.length} running</span>
                </div>
                {rows.map(c => (
                  <ServiceRow key={c.name} c={c} logs={logs} acting={acting}
                    onFetchLogs={onFetchLogs} onRestartContainer={onRestartContainer} />
                ))}
              </div>
            );
          })}
        </div>

        <div style={{display:'flex',flexDirection:'column',gap:16}}>
          <div className="card admin-card">
            <div className="admin-card-title">System</div>
            <div className="admin-stats">
              <div className="admin-stat"><div className="admin-stat-val">{data?.counts?.clients ?? '—'}</div><div className="admin-stat-label">Clients</div></div>
              <div className="admin-stat"><div className="admin-stat-val">{data?.counts?.users ?? '—'}</div><div className="admin-stat-label">Users</div></div>
              <div className="admin-stat"><div className="admin-stat-val">{data?.counts?.active_scans ?? '—'}</div><div className="admin-stat-label">Active Scans</div></div>
              <div className="admin-stat">
                <div className={`admin-stat-val ${data?.redis?.connected ? '' : 'admin-stat-err'}`}>{data?.redis?.connected ? '✓' : '✗'}</div>
                <div className="admin-stat-label">Redis</div>
              </div>
            </div>
          </div>

          <div className="card admin-card">
            <div className="admin-card-title">Disk Usage</div>
            <div className="admin-stats">
              <div className="admin-stat"><div className="admin-stat-val">{data?.disk?.used ?? '—'}</div><div className="admin-stat-label">Used</div></div>
              <div className="admin-stat"><div className="admin-stat-val">{data?.disk?.avail ?? '—'}</div><div className="admin-stat-label">Available</div></div>
              <div className="admin-stat"><div className="admin-stat-val">{data?.disk?.total ?? '—'}</div><div className="admin-stat-label">Total</div></div>
              <div className="admin-stat"><div className="admin-stat-val">{data?.disk?.pct ?? '—'}</div><div className="admin-stat-label">Used %</div></div>
            </div>
          </div>

          {failedScans.length > 0 && (
            <div className="card admin-card">
              <div className="admin-card-title">Failed Scans</div>
              <div style={{display:'flex',flexDirection:'column',gap:0}}>
                {failedScans.map(scan => (
                  <div key={scan.slug} style={{borderBottom:'1px solid var(--border)',padding:'12px 0',display:'flex',alignItems:'center',justifyContent:'space-between',gap:12}}>
                    <div>
                      <div style={{fontSize:13,fontWeight:600,color:'var(--text)'}}>{scan.client_name.replace(/_/g,' ')}</div>
                      <div style={{fontSize:11,fontFamily:'var(--font-mono)',color:'var(--red)',marginTop:2}}>
                        {scan.cancelled ? 'Cancelled' : `Exit code ${scan.exit_code}`}
                        {scan.initiated_by ? ` · by ${scan.initiated_by}` : ''}
                        {scan.started ? ` · ${fmtTime(scan.started)}` : ''}
                      </div>
                    </div>
                    <button className="btn btn-danger btn-sm" style={{ flexShrink: 0 }}
                      disabled={acting[`clear-${scan.slug}`]} onClick={() => onClearFailedScan(scan.slug)}>
                      {acting[`clear-${scan.slug}`] ? <span className="spinner" /> : 'Clear'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <EdgeFleetCard fleet={fleet} />

          <VersionsCard versions={versions} />

          <MaintenanceCard data={data} maint={maint} acting={acting}
            onClearLogs={onClearLogs} onPrune={onPrune} onTrim={onTrim} onRecalc={onRecalc} />
        </div>
      </div>

      {(buildingApi || apiBuildDone || apiBuildLog.length > 0) && (
        <div className="card admin-card" style={{marginTop:16}}>
          <div className="admin-card-title" style={{marginBottom:10}}>
            {buildingApi ? <><span className="pulse-dot" style={{display:'inline-block',marginRight:6}} />Building API...</> : apiBuildDone ? '✓ API build complete — restarting within 60s' : 'API build log'}
          </div>
          <pre ref={apiBuildLogRef} style={{background:'#0a0a0a',color:'#e5e5ea',fontSize:11,fontFamily:'var(--font-mono)',padding:12,borderRadius:6,maxHeight:300,overflow:'auto',margin:0,whiteSpace:'pre-wrap'}}>
            {apiBuildLog.join('\n') || 'Starting build...'}
          </pre>
        </div>
      )}

      {(building || buildDone || buildLog.length > 0) && (
        <div className="card admin-card" style={{marginTop:16}}>
          <div className="admin-card-title" style={{marginBottom:10}}>
            {building ? <><span className="pulse-dot" style={{display:'inline-block',marginRight:6}} />Building frontend...</> : buildDone ? '✓ Build complete' : 'Build log'}
          </div>
          <pre ref={buildLogRef} style={{background:'#0a0a0a',color:'#e5e5ea',fontSize:11,fontFamily:'var(--font-mono)',padding:12,borderRadius:6,maxHeight:300,overflow:'auto',margin:0,whiteSpace:'pre-wrap'}}>
            {buildLog.join('\n') || 'Starting build...'}
          </pre>
        </div>
      )}
    </>
  );
}

// ── Versions card (2.13) ──────────────────────────────────────────────────────

function ageMinutes(iso) {
  if (!iso) return null;
  const str = /(Z|[+-]\d\d:?\d\d)$/i.test(iso) ? iso : `${iso}Z`;
  const t = new Date(str).getTime();
  return isNaN(t) ? null : (Date.now() - t) / 60000;
}

function KvRow({ label, value, sub, rag }) {
  return (
    <div className="admin-kv-row">
      <div className="admin-kv-label">
        {rag && <StatusDot rag={rag} />}
        <span>{label}</span>
      </div>
      <div className="admin-kv-value">
        <div>{value}</div>
        {sub && <div className="admin-kv-sub" style={{color: rag && rag !== 'green' ? RAG_COLOUR[rag] : undefined}}>{sub}</div>}
      </div>
    </div>
  );
}

function VersionsCard({ versions }) {
  if (!versions) return null;
  const { api = {}, frontend = {}, compose = {}, edge_dist = {}, watchdog = {} } = versions;

  const apiRag = api.match === false ? 'amber' : api.running ? 'green' : 'amber';
  const apiSub = api.match === false
    ? `NAS copy is v${api.nas} — use Rebuild API to deploy it`
    : api.nas ? 'Matches the NAS copy' : 'NAS copy not readable';

  const feRag = frontend.match === false ? 'amber' : frontend.served ? 'green' : 'amber';
  const feSub = frontend.match === false
    ? `Built bundle ${frontend.built} is not being served — use Sync Frontend`
    : frontend.built_at ? `Built ${fmtTime(frontend.built_at)}` : '';

  const edBehind = edge_dist.behind || [];
  const edMissing = edge_dist.not_reported || [];
  const edgeRag = edge_dist.error || !edge_dist.files ? 'amber' : (edBehind.length || edMissing.length) ? 'amber' : 'green';
  const edgeValue = edge_dist.error ? 'Unknown' : !edge_dist.files ? 'No files'
    : edBehind.length ? `${edBehind.length} node${edBehind.length !== 1 ? 's' : ''} behind`
    : edMissing.length ? `${edMissing.length} not reporting`
    : `All ${edge_dist.nodes} node${edge_dist.nodes !== 1 ? 's' : ''} current`;
  const edgeSub = edge_dist.error ? edge_dist.error : !edge_dist.files ? 'edge-dist holds no scripts for edge nodes'
    : (edBehind.length || edMissing.length) ? (
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {edBehind.map(n => (
          <Link key={n.edge_id} to={`/edge-nodes?node=${encodeURIComponent(n.edge_id)}&tab=scripts`} style={{ color: 'inherit' }}>
            {n.label}{n.online ? '' : ' (offline)'}: {n.files.map(f => `${f.file} ${f.have} → ${f.latest}`).join(', ')}
          </Link>
        ))}
        {edMissing.map(n => (
          <Link key={n.edge_id} to={`/edge-nodes?node=${encodeURIComponent(n.edge_id)}&tab=scripts`} style={{ color: 'inherit' }}>
            {n.label}: no script versions reported
          </Link>
        ))}
      </span>
    )
    : `${edge_dist.files} files${edge_dist.newest_file ? `, newest copied ${fmtTime(edge_dist.newest_file)}` : ''}`;

  const wdAge = ageMinutes(watchdog.last_run);
  const wdRag = wdAge === null ? 'red' : wdAge > 30 ? 'red' : wdAge > 5 ? 'amber' : 'green';
  const wdSub = watchdog.last_run
    ? `Last ran ${fmtTime(watchdog.last_run)}${wdAge > 5 ? ' — Rebuild API and API restarts will not happen' : ''}`
    : 'No watchdog log found';

  return (
    <div className="card admin-card">
      <div className="admin-card-title">Versions</div>
      <KvRow label="Portal API" rag={apiRag} value={api.running ? `v${api.running}` : 'Unknown'} sub={apiSub} />
      <KvRow label="Frontend bundle" rag={feRag}
        value={<span className="admin-mono">{frontend.served || 'Unknown'}</span>} sub={feSub} />
      <KvRow label="compose.yml" rag={compose.rag || 'grey'} value={compose.version ? `v${compose.version}` : 'Unknown'}
        sub={compose.rag === 'amber' ? compose.note
          : [compose.modified ? `Modified ${fmtTime(compose.modified)}` : '', compose.rag === 'grey' ? compose.note : '']
              .filter(Boolean).join(' — ')} />
      <KvRow label="Edge scripts" rag={edgeRag}
        value={<Link to="/edge-nodes" style={{ color: 'inherit', textDecoration: 'none' }}>{edgeValue}</Link>}
        sub={edgeSub} />
      <KvRow label="Watchdog" rag={wdRag}
        value={watchdog.log_mb != null ? `Log ${watchdog.log_mb} MB` : ''} sub={wdSub} />
    </div>
  );
}

// ── Integrations tab (2.13) ───────────────────────────────────────────────────

function MailCard({ mail }) {
  if (!mail) return null;
  if (mail.error) return <div className="card admin-card"><div className="admin-card-title">Mail</div><div className="error-msg">{mail.error}</div></div>;
  const p = mail.email_poller || {};
  const r = mail.mail_relay || {};
  const pollAge = ageMinutes(p.last_log);
  const pollRag = !p.last_log ? 'red' : pollAge > 60 ? 'amber' : 'green';
  const relayRag = r.queue === null || r.queue === undefined ? 'amber'
    : r.queue > 0 ? 'amber'
    : r.last_status && r.last_status !== 'sent' ? 'amber' : 'green';
  return (
    <div className="card admin-card">
      <div className="admin-card-title">Mail</div>
      <KvRow label="Email Poller" rag={pollRag}
        value={p.last_log ? `Last log ${fmtTime(p.last_log)}` : 'No log output'}
        sub={[p.last_line, p.recent_errors ? `${p.recent_errors} lines mentioning error in the last 200` : '']
          .filter(Boolean).join(' — ')} />
      <KvRow label="Mail Relay" rag={relayRag}
        value={r.queue === null || r.queue === undefined ? 'Queue unknown'
          : r.queue === 0 ? 'Queue empty' : `${r.queue} message${r.queue === 1 ? '' : 's'} queued`}
        sub={r.last_delivery
          ? `Last delivery ${fmtTime(r.last_delivery)}: ${r.last_status || 'unknown status'}`
          : 'No deliveries in recent logs'} />
    </div>
  );
}

function IntegrationRow({ row, testing, onTest }) {
  const t = row.test;
  return (
    <div className="admin-int-row">
      <div className="admin-int-var">
        <StatusDot rag={row.rag} />
        <div style={{minWidth:0}}>
          <div className="admin-mono admin-int-name">{row.var}</div>
          <div className="admin-service-meta">
            {row.set ? `Set in .env${row.hint ? ` ${row.hint}` : ''}` : 'Not in .env'}
          </div>
        </div>
      </div>
      <div className="admin-int-uses">
        {row.uses.length
          ? row.uses.map(u => (
              <div key={`${u.service}-${u.container_var || 'cmd'}`}>
                {u.label}
                {u.container_var && u.container_var !== row.var && <span className="admin-service-meta"> as {u.container_var}</span>}
                {u.context === 'command' && <span className="admin-service-meta"> (command line)</span>}
              </div>
            ))
          : <span className="admin-service-meta">No service</span>}
      </div>
      <div className="admin-int-status">
        {row.note && <div className="admin-service-note" style={{color: RAG_COLOUR[row.rag], marginTop:0}}>{row.note}</div>}
        {t && (
          <div className="admin-service-note" style={{color: t.ok ? 'var(--green)' : 'var(--red)'}}>
            {t.ok ? '✓' : '✗'} {t.detail}
            <span className="admin-service-meta"> · tested {fmtTime(t.tested)}</span>
          </div>
        )}
        {!row.note && !t && <span className="admin-service-meta">OK</span>}
      </div>
      <div className="admin-int-actions">
        {row.testable && row.set && (
          <button className="btn btn-secondary btn-sm" disabled={testing}
            title={row.test_note || undefined} onClick={() => onTest(row)}>
            {testing ? <span className="spinner" /> : 'Test'}
          </button>
        )}
      </div>
    </div>
  );
}

function IntegrationsTab() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('attention');
  const [testing, setTesting] = useState({});
  const [flash, setFlash]     = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    authFetch('/api/admin/integrations').then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setLoading(false); setFlash({ msg: 'Could not load integrations', ok: false }); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const runTest = async (row) => {
    if (row.test_note && !window.confirm(`Test ${row.var}?\n\n${row.test_note}.`)) return;
    setTesting(t => ({ ...t, [row.var]: true }));
    try {
      const res = await authFetch(`/api/admin/integrations/test/${row.var}`, { method: 'POST' });
      const d = await res.json();
      if (!res.ok) { setFlash({ msg: d.error || 'Test failed', ok: false }); return; }
      setData(prev => prev && ({ ...prev, rows: prev.rows.map(r => r.var === row.var ? { ...r, test: d } : r) }));
    } catch (e) {
      setFlash({ msg: e.message, ok: false });
    } finally {
      setTesting(t => ({ ...t, [row.var]: false }));
    }
  };

  const testAll = async () => {
    const rows = (data?.rows || []).filter(r => r.testable && r.set);
    const costly = rows.filter(r => r.test_note).map(r => `${r.var}: ${r.test_note}`);
    if (!window.confirm(`Test ${rows.length} keys?${costly.length ? `\n\n${costly.join('\n')}` : ''}`)) return;
    for (const r of rows) {
      setTesting(t => ({ ...t, [r.var]: true }));
      try {
        const res = await authFetch(`/api/admin/integrations/test/${r.var}`, { method: 'POST' });
        const d = await res.json();
        if (res.ok) setData(prev => prev && ({ ...prev, rows: prev.rows.map(x => x.var === r.var ? { ...x, test: d } : x) }));
      } catch (e) { /* carry on with the next key */ }
      setTesting(t => ({ ...t, [r.var]: false }));
    }
  };

  if (loading && !data) return <div style={{padding:48,textAlign:'center'}}><span className="spinner" /></div>;
  if (!data) return flash ? <div className="error-msg">{flash.msg}</div> : null;

  const failedTests = data.rows.filter(r => r.test && !r.test.ok).length;
  const visible = filter === 'attention'
    ? data.rows.filter(r => r.rag !== 'green' || (r.test && !r.test.ok))
    : filter === 'testable' ? data.rows.filter(r => r.testable)
    : data.rows;

  return (
    <>
      {flash && <div className={flash.ok ? 'success-msg' : 'error-msg'} style={{marginBottom:12}}>{flash.msg}</div>}
      <div className="admin-grid">
        <div className="card admin-card">
          <div className="admin-services-head">
            <div className="admin-card-title" style={{marginBottom:0}}>Environment and API keys</div>
            <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
              {data.generated && <div className="admin-service-meta">Updated {fmtTime(data.generated)}</div>}
              <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}>
                {loading ? <span className="spinner" /> : '↻ Refresh'}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={testAll}>Test all keys</button>
            </div>
          </div>
          <div className="admin-services-summary">
            {data.summary.total} variables
            {data.summary.red > 0 && <span style={{color:'var(--red)'}}> · {data.summary.red} problems</span>}
            {data.summary.amber > 0 && <span style={{color:'var(--amber)'}}> · {data.summary.amber} warnings</span>}
            {failedTests > 0 && <span style={{color:'var(--red)'}}> · {failedTests} failed key tests</span>}
            {!data.docker_ok && <span style={{color:'var(--amber)'}}> · Docker unreachable, containers not checked</span>}
          </div>
          <div className="admin-int-filters">
            {[['attention', 'Needs attention'], ['testable', 'Testable keys'], ['all', 'All variables']].map(([id, label]) => (
              <button key={id} className={`admin-pill ${filter === id ? 'admin-pill-active' : ''}`} onClick={() => setFilter(id)}>{label}</button>
            ))}
          </div>
          {visible.length === 0
            ? <div className="admin-service-meta" style={{padding:'16px 0'}}>Nothing needs attention.</div>
            : visible.map(r => <IntegrationRow key={r.var} row={r} testing={!!testing[r.var]} onTest={runTest} />)}
          {data.env_files?.length > 0 && (
            <div className="admin-service-meta" style={{marginTop:14}}>
              Not checked here: {data.env_files.map(f => `${f.label} reads its own ${f.path}`).join('; ')}.
            </div>
          )}
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:16}}>
          <MailCard mail={data.mail} />
        </div>
      </div>
    </>
  );
}

// ── Phase 3 components (2.15) ─────────────────────────────────────────────────

function fmtBytes(n) {
  if (n === null || n === undefined) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = Number(n), i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v >= 10 || i === 0 ? v.toFixed(0) : v.toFixed(1)} ${units[i]}`;
}

function EdgeFleetCard({ fleet }) {
  if (!fleet) return null;
  const { nodes = [], summary = {} } = fleet;
  return (
    <div className="card admin-card">
      <div className="admin-services-head">
        <div className="admin-card-title" style={{marginBottom:0}}>Edge Fleet</div>
        <div className="admin-service-meta">{summary.online ?? 0} of {summary.total ?? 0} online</div>
      </div>
      {nodes.length === 0 && <div className="admin-service-meta" style={{marginTop:10}}>No edge nodes registered.</div>}
      {nodes.map(n => (
        <div key={n.edge_id} className="admin-fleet-row">
          <div className="admin-fleet-head">
            <StatusDot rag={n.rag} />
            <span className="admin-fleet-name">{n.label}</span>
            <span className="admin-mono admin-fleet-id">{n.short_id}</span>
          </div>
          <div className="admin-fleet-body">
            {n.issues.length > 0
              ? n.issues.map((t, i) => (
                  <div key={i} style={{color: RAG_COLOUR[n.rag]}}>{t}</div>
                ))
              : <div>Online, last heartbeat {fmtTime(n.last_heartbeat)}</div>}
            {(n.node_check?.dsm_version || (n.info || []).length > 0) && (
              <div className="admin-fleet-info">
                {[n.node_check?.dsm_version, ...(n.info || [])].filter(Boolean).join(' · ')}
              </div>
            )}
          </div>
        </div>
      ))}
      {summary.attention > 0 && (
        <div className="admin-service-meta" style={{marginTop:8}}>Details and actions are on the Edge Nodes page.</div>
      )}
    </div>
  );
}

function MaintenanceCard({ data, maint, acting, onClearLogs, onPrune, onTrim, onRecalc }) {
  const img = maint?.images;
  const r   = maint?.redis;
  const st  = maint?.storage;
  const saveOk = r?.last_save_status ? r.last_save_status === 'ok' : true;
  return (
    <div className="card admin-card">
      <div className="admin-card-title">Maintenance</div>

      <div className="admin-maint-row">
        <div>
          <div className="admin-maint-title">Temporary Log Files</div>
          <div className="admin-maint-sub">{data?.counts?.tmp_logs ?? 0} files, {data?.counts?.tmp_logs_mb ?? 0} MB</div>
        </div>
        <button className="btn btn-secondary btn-sm admin-maint-btn" disabled={acting['clear'] || !data?.counts?.tmp_logs} onClick={onClearLogs}>
          {acting['clear'] ? <span className="spinner" /> : 'Clear'}
        </button>
      </div>

      <div className="admin-maint-row">
        <div>
          <div className="admin-maint-title">Dangling Docker Images</div>
          <div className="admin-maint-sub">
            {img ? `${img.count} image${img.count === 1 ? '' : 's'}, ${fmtBytes(img.bytes)} (all stacks on this NAS)` : 'Unknown'}
          </div>
        </div>
        <button className="btn btn-secondary btn-sm admin-maint-btn" disabled={acting['prune'] || !img?.count} onClick={onPrune}>
          {acting['prune'] ? <span className="spinner" /> : 'Prune'}
        </button>
      </div>

      <div className="admin-maint-row">
        <div>
          <div className="admin-maint-title">Watchdog Log</div>
          <div className="admin-maint-sub">
            {maint?.watchdog_log_bytes != null ? `${fmtBytes(maint.watchdog_log_bytes)}, grows by about 2 lines a minute` : 'Not found'}
          </div>
        </div>
        <button className="btn btn-secondary btn-sm admin-maint-btn" disabled={acting['trim'] || !maint?.watchdog_log_bytes} onClick={onTrim}>
          {acting['trim'] ? <span className="spinner" /> : 'Trim'}
        </button>
      </div>

      <div className="admin-maint-row">
        <div style={{minWidth:0}}>
          <div className="admin-maint-title">
            {r && !(r.available && saveOk) && <StatusDot rag="red" />} Redis
          </div>
          <div className="admin-maint-sub">
            {!r ? 'Unknown'
              : !r.available ? `Unavailable${r.error ? `: ${r.error}` : ''}`
              : `${r.used_memory} used (peak ${r.peak_memory}), ${r.keys} keys`}
          </div>
          {r?.available && (
            <div className="admin-maint-sub" style={{color: saveOk ? undefined : 'var(--red)'}}>
              Last saved {fmtTime(r.last_save)}{saveOk ? '' : ` — last save ${r.last_save_status}`}
            </div>
          )}
        </div>
      </div>

      <div className="admin-maint-row" style={{alignItems:'flex-start'}}>
        <div style={{flex:1,minWidth:0}}>
          <div className="admin-maint-title">Storage</div>
          {st ? (
            <div className="admin-storage-grid">
              <span>Client data</span><span>{fmtBytes(st.clients_total)}</span>
              <span>Analytics</span><span>{fmtBytes(st.analytics)}</span>
              <span>Reports and archive</span><span>{fmtBytes(st.reports)}</span>
              <span>Ticket attachments</span><span>{fmtBytes(st.ticket_attachments)}</span>
            </div>
          ) : <div className="admin-maint-sub">Unknown</div>}
          {st?.measured && <div className="admin-maint-sub">Measured {fmtTime(st.measured)}</div>}
        </div>
        <button className="btn btn-secondary btn-sm admin-maint-btn" disabled={acting['recalc']} onClick={onRecalc}>
          {acting['recalc'] ? <span className="spinner" /> : 'Recalculate'}
        </button>
      </div>
    </div>
  );
}

function JobsTab({ jobs, loading, onRefresh }) {
  const [filter, setFilter] = useState('attention');
  if (loading && !jobs) return <div style={{padding:48,textAlign:'center'}}><span className="spinner" /></div>;
  if (!jobs) return <div className="error-msg">Could not load background jobs</div>;
  const rows = filter === 'attention' ? jobs.jobs.filter(j => j.rag !== 'green') : jobs.jobs;
  const groups = [...new Set(jobs.jobs.map(j => j.group))];
  return (
    <div className="card admin-card">
      <div className="admin-services-head">
        <div className="admin-card-title" style={{marginBottom:0}}>Background Jobs</div>
        <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
          {jobs.generated && <div className="admin-service-meta">Updated {fmtTime(jobs.generated)}</div>}
          <button className="btn btn-secondary btn-sm" onClick={onRefresh} disabled={loading}>
            {loading ? <span className="spinner" /> : '↻ Refresh'}
          </button>
        </div>
      </div>
      <div className="admin-services-summary">
        {jobs.summary.total} jobs in the Portal API
        {jobs.summary.red > 0 && <span style={{color:'var(--red)'}}> · {jobs.summary.red} stopped or stuck</span>}
        {jobs.summary.amber > 0 && <span style={{color:'var(--amber)'}}> · {jobs.summary.amber} missed a run</span>}
        {jobs.api_started && <> · API started {fmtTime(jobs.api_started)}</>}
      </div>
      <div className="admin-int-filters">
        {[['attention', 'Needs attention'], ['all', 'All jobs']].map(([id, label]) => (
          <button key={id} className={`admin-pill ${filter === id ? 'admin-pill-active' : ''}`} onClick={() => setFilter(id)}>{label}</button>
        ))}
      </div>
      {rows.length === 0 && <div className="admin-service-meta" style={{padding:'16px 0'}}>All background jobs are running normally.</div>}
      {groups.map(g => {
        const gr = rows.filter(j => j.group === g);
        if (!gr.length) return null;
        return (
          <div key={g} className="admin-service-group">
            <div className="admin-service-group-head"><span>{g}</span><span className="admin-service-group-count">{gr.length}</span></div>
            {gr.map(j => (
              <div key={j.id} className="admin-job-row">
                <div className="admin-job-name">
                  <StatusDot rag={j.rag} />
                  <div style={{minWidth:0}}>
                    <div className="admin-service-label" style={{fontSize:13}}>{j.label}</div>
                    <div className="admin-service-meta">{j.schedule || `Continuous, every ${j.loop_secs >= 60 ? `${Math.round(j.loop_secs / 60)} min` : `${j.loop_secs}s`}`}</div>
                  </div>
                </div>
                <div className="admin-job-times">
                  {j.kind === 'scheduled' && (
                    <div>Last triggered {j.last_run?.at ? fmtTime(j.last_run.at) : 'not recorded'}
                      {j.last_run?.detail && <span className="admin-service-meta"> ({j.last_run.detail})</span>}
                    </div>
                  )}
                  <div className="admin-service-meta">Last looped {j.last_tick ? fmtTime(j.last_tick) : 'not yet'}</div>
                  {j.note && <div className="admin-service-note" style={{color: j.rag === 'green' ? 'var(--text-muted)' : RAG_COLOUR[j.rag]}}>{j.note}</div>}
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

// ── Add mention row component ──────────────────────────────────────────────────

function AddMentionRow({ onAdd }) {
  const [name,  setName]  = React.useState('');
  const [email, setEmail] = React.useState('');

  const handleAdd = () => {
    const e = email.trim();
    const n = name.trim() || e.split('@')[0];
    if (!e || !e.includes('@')) return;
    onAdd({ name: n, email: e });
    setName(''); setEmail('');
  };

  return (
    <div style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap'}}>
      <input className="input-sm" value={name} onChange={e => setName(e.target.value)} placeholder="Display name"
        style={{ flex:'1 1 120px' }} />
      <input className="input-sm" value={email} onChange={e => setEmail(e.target.value)} placeholder="email@stg.limited"
        type="email"
        style={{ flex:'2 1 180px' }}
        onKeyDown={e => e.key === 'Enter' && handleAdd()} />
      <button className="btn btn-secondary btn-sm" style={{ flexShrink: 0 }} onClick={handleAdd}>
        Add
      </button>
    </div>
  );
}

// ── Health Alerts tab ─────────────────────────────────────────────────────────

function AlertsTab() {
  const [config, setConfig]   = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [testing, setTesting] = useState(false);
  const [msg,     setMsg]     = useState(null);
  const [openAlert, setOpenAlert] = useState(null);

  const load = useCallback(() => {
    Promise.all([
      authFetch('/api/admin/alerts/config').then(r => r.json()),
      authFetch('/api/admin/alerts/history').then(r => r.json()),
    ]).then(([cfg, hist]) => {
      setConfig(cfg);
      setHistory(hist.history || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (text, ok = true) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 5000);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await authFetch('/api/admin/alerts/config', { method: 'PUT', body: JSON.stringify(config) });
      const d = await res.json();
      flash(d.message || d.error, res.ok);
    } catch(e) { flash(e.message, false); }
    finally { setSaving(false); }
  };

  const sendTest = async (channel) => {
    setTesting(true);
    try {
      const res = await authFetch('/api/admin/alerts/test', { method: 'POST', body: JSON.stringify({ channel }) });
      const d = await res.json();
      flash(d.message || d.error, res.ok);
      setTimeout(load, 2000); // refresh history
    } catch(e) { flash(e.message, false); }
    finally { setTesting(false); }
  };

  const set = (key, val) => setConfig(c => ({ ...c, [key]: val }));

  const sevColor = { error: '#ff2d55', warning: '#e6b800', info: '#30d158' };

  if (loading) return <div style={{padding:48,textAlign:'center'}}><span className="spinner" /></div>;

  return (
    <div className="alerts-grid">
      <div style={{display:'flex',flexDirection:'column',gap:16,minWidth:0}}>

        {/* Teams webhook */}
        <div className="card admin-card">
          <div className="admin-card-title">Microsoft Teams</div>
          <div style={{fontSize:12,color:'var(--text-muted)',marginBottom:12,lineHeight:1.5}}>
            Create a "Platform Alerts" channel in Teams, add an Incoming Webhook connector, and paste the URL here.
          </div>
          <label style={{fontSize:11,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',display:'block',marginBottom:6}}>
            Webhook URL
          </label>
          <input className="input-sm"
            type="url"
            value={config?.teams_webhook_url || ''}
            onChange={e => set('teams_webhook_url', e.target.value)}
            placeholder="https://stglimited.webhook.office.com/..."
            style={{ width:'100%' }}
          />
          <div style={{display:'flex',gap:8,marginTop:12,flexWrap:'wrap'}}>
            <button className="btn btn-primary btn-sm" disabled={saving} onClick={save}>
              {saving ? <span className="spinner" /> : 'Save'}
            </button>
            <button className="btn btn-secondary btn-sm" disabled={testing || !config?.teams_webhook_url}
              onClick={() => sendTest('teams')}>
              {testing ? <span className="spinner" /> : 'Send Test to Teams'}
            </button>
            <button className="btn btn-secondary btn-sm" disabled={testing || !config?.teams_webhook_url}
              onClick={() => sendTest('email')}>
              {testing ? <span className="spinner" /> : 'Send Test by Email'}
            </button>
          </div>
          {msg && <div className={msg.ok ? 'success-msg' : 'error-msg'} style={{marginTop:10}}>{msg.text}</div>}
        </div>

        {/* Alert channels */}
        <div className="card admin-card">
          <div className="admin-card-title">Immediate Alert Channels</div>
          <Toggle checked={!!config?.teams_alerts} onChange={v => set('teams_alerts', v)}
            label="Microsoft Teams" sublabel="Send immediate alerts to the configured Teams webhook" />
          <Toggle checked={!!config?.email_alerts} onChange={v => set('email_alerts', v)}
            label="Email" sublabel="Send immediate alerts to all admin email addresses" />
          <div style={{marginTop:14}}>
            <button className="btn btn-primary btn-sm" disabled={saving} onClick={save}>
              {saving ? <span className="spinner" /> : 'Save'}
            </button>
          </div>
        </div>

        {/* Digest channels */}
        <div className="card admin-card">
          <div className="admin-card-title">Morning Digest Channels</div>
          <Toggle checked={!!config?.digest_teams} onChange={v => set('digest_teams', v)}
            label="Microsoft Teams" sublabel="Send the daily digest to the configured Teams webhook" />
          <Toggle checked={!!config?.digest_email} onChange={v => set('digest_email', v)}
            label="Email" sublabel="Send the daily digest to all admin email addresses" />
          <div style={{marginTop:14}}>
            <button className="btn btn-primary btn-sm" disabled={saving} onClick={save}>
              {saving ? <span className="spinner" /> : 'Save'}
            </button>
          </div>
        </div>

        {/* Alert triggers */}
        <div className="card admin-card">
          <div className="admin-card-title">Alert Triggers</div>
          <Toggle checked={!!config?.alert_scan_failure} onChange={v => set('alert_scan_failure', v)}
            label="Scan failures" sublabel="Alert when a scheduled cyber scan fails or times out" />
          <Toggle checked={!!config?.alert_edge_offline} onChange={v => set('alert_edge_offline', v)}
            label="Edge node offline" sublabel="Alert when an Edge node stops sending heartbeats" />
          <Toggle checked={config?.alert_network_cve !== false} onChange={v => set('alert_network_cve', v)}
            label="Critical vulnerabilities on client devices" sublabel="Alert when an Edge network scan finds a critical CVE not seen before on a device" />
          <Toggle checked={!!config?.alert_container_restart} onChange={v => set('alert_container_restart', v)}
            label="Container restarts" sublabel="Alert when a Core service container restarts unexpectedly" />
          <Toggle checked={!!config?.alert_disk_threshold} onChange={v => set('alert_disk_threshold', v)}
            label="Disk threshold" sublabel={`Alert when disk usage exceeds ${config?.disk_threshold_pct ?? 85}%`} />
          {config?.alert_disk_threshold && (
            <div style={{padding:'12px 0',borderBottom:'1px solid var(--border)'}}>
              <label style={{fontSize:11,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',display:'block',marginBottom:8}}>
                Disk threshold: {config?.disk_threshold_pct ?? 85}%
              </label>
              <input type="range" min={50} max={95} step={5}
                value={config?.disk_threshold_pct ?? 85}
                onChange={e => set('disk_threshold_pct', Number(e.target.value))}
                style={{width:'100%',accentColor:'var(--accent)'}}
              />
              <div style={{display:'flex',justifyContent:'space-between',fontSize:10,color:'var(--text-muted)',marginTop:4}}>
                <span>50%</span><span>95%</span>
              </div>
            </div>
          )}
          <div style={{marginTop:14}}>
            <button className="btn btn-primary btn-sm" disabled={saving} onClick={save}>
              {saving ? <span className="spinner" /> : 'Save'}
            </button>
          </div>
        </div>

        {/* Alert mentions */}
        <div className="card admin-card">
          <div className="admin-card-title">Teams Mentions</div>
          <div style={{fontSize:12,color:'var(--text-muted)',marginBottom:12,lineHeight:1.5}}>
            These people will be @mentioned in every Teams alert. Use their Microsoft 365 email address.
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:0,marginBottom:12}}>
            {(config?.alert_mentions || []).map((m, i) => (
              <div key={i} style={{display:'flex',alignItems:'center',gap:8,padding:'8px 0',borderBottom:'1px solid var(--border)'}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:600,color:'var(--text)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{m.name || m.email}</div>
                  <div style={{fontSize:11,color:'var(--text-muted)',fontFamily:'var(--font-mono)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{m.email}</div>
                </div>
                <button className="btn btn-danger btn-sm" style={{ flexShrink: 0 }}
                  onClick={() => set('alert_mentions', (config.alert_mentions || []).filter((_, j) => j !== i))}>
                  Remove
                </button>
              </div>
            ))}
            {(config?.alert_mentions || []).length === 0 && (
              <div style={{fontSize:12,color:'var(--text-muted)',padding:'8px 0'}}>No mentions configured</div>
            )}
          </div>
          <AddMentionRow onAdd={m => set('alert_mentions', [...(config?.alert_mentions || []), m])} />
          <div style={{marginTop:14}}>
            <button className="btn btn-primary btn-sm" disabled={saving} onClick={save}>
              {saving ? <span className="spinner" /> : 'Save'}
            </button>
          </div>
        </div>

        {/* Morning digest */}
        <div className="card admin-card">
          <div className="admin-card-title">Morning Digest</div>
          <Toggle checked={!!config?.digest_enabled} onChange={v => set('digest_enabled', v)}
            label="Daily morning digest" sublabel="Send a daily health summary to Teams and email" />
          {config?.digest_enabled && (
            <div style={{padding:'12px 0',borderBottom:'1px solid var(--border)'}}>
              <label style={{fontSize:11,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',display:'block',marginBottom:6}}>
              Send time (GMT)
              </label>
              <input className="input-sm" type="time" value={config?.digest_time || '07:00'} onChange={e => set('digest_time', e.target.value)}/>
            </div>
          )}
          <div style={{marginTop:14,display:'flex',gap:8}}>
            <button className="btn btn-primary btn-sm" disabled={saving} onClick={save}>
              {saving ? <span className="spinner" /> : 'Save'}
            </button>
            <button className="btn btn-secondary btn-sm" disabled={testing}
              onClick={() => sendTest('both')}>
              {testing ? <span className="spinner" /> : 'Send Digest Now'}
            </button>
          </div>
        </div>
      </div>

      {/* Alert history */}
      <div className="card admin-card">
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
          <div className="admin-card-title" style={{marginBottom:0}}>Recent Alerts</div>
          <button className="btn btn-secondary btn-sm" onClick={load}>↻</button>
        </div>
        {history.length === 0 ? (
          <div style={{fontSize:12,color:'var(--text-muted)',textAlign:'center',padding:'24px 0'}}>No alerts sent yet</div>
        ) : (
          <div style={{display:'flex',flexDirection:'column',gap:0}}>
            {history.slice(0, 15).map((h, i) => {
              const f = h.facts || {};
              const shown = Object.entries(f).filter(([k]) => !['Open in Clearview'].includes(k)).slice(0, 6);
              const open = openAlert === i;
              return (
              <div key={i} style={{padding:'10px 0',borderBottom:i < history.length - 1 ? '1px solid var(--border)' : 'none'}}>
                <div style={{display:'flex',alignItems:'flex-start',gap:8,marginBottom:4,cursor:(shown.length || h.details?.length) ? 'pointer' : 'default'}}
                  onClick={() => setOpenAlert(open ? null : i)}>
                  <div style={{width:8,height:8,borderRadius:'50%',background:sevColor[h.severity] || '#86868b',flexShrink:0,marginTop:4}} />
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12,fontWeight:600,color:'var(--text)',wordBreak:'break-word'}}>{h.title}</div>
                    <div style={{fontSize:10,color:'var(--text-muted)',marginTop:2}}>
                      {h.ts ? new Date(h.ts).toLocaleString('en-GB', {day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit',timeZone:'Europe/London'}) + ' GMT' : ''}
                      {h.triggered_by ? ` · ${h.triggered_by}` : ''}
                      {h.channel ? ` · ${h.channel.charAt(0).toUpperCase() + h.channel.slice(1)}` : ''}
                    </div>
                  </div>
                  {(shown.length > 0 || h.details?.length > 0) && <span style={{fontSize:10,color:'var(--text-muted)',flexShrink:0}}>{open ? '▲' : '▼'}</span>}
                </div>
                {open && (
                  <div style={{marginLeft:16,marginTop:6,fontSize:11,color:'var(--text-muted)',display:'flex',flexDirection:'column',gap:6}}>
                    {h.message && <div style={{lineHeight:1.5}}>{h.message}</div>}
                    {shown.length > 0 && (
                      <div style={{display:'grid',gridTemplateColumns:'auto 1fr',gap:'2px 10px'}}>
                        {shown.map(([k, v]) => <React.Fragment key={k}><span>{k}</span><span style={{color:'var(--text)',wordBreak:'break-word'}}>{String(v)}</span></React.Fragment>)}
                      </div>
                    )}
                    {(h.details || []).map((d, j) => (
                      <div key={j}>
                        <div style={{fontWeight:600,color:'var(--text)',wordBreak:'break-word'}}>{d.heading}</div>
                        {(d.lines || []).map((l, k) => <div key={k} style={{wordBreak:'break-word'}}>{l}</div>)}
                      </div>
                    ))}
                  </div>
                )}
                {h.link && (
                  <div style={{marginLeft:16,marginTop:4}}>
                    <Link to={h.link} style={{fontSize:11,fontWeight:600,color:'var(--accent)',textDecoration:'none'}}>
                      {h.client_name ? `Open ${h.client_name}'s devices` : 'Open'} →
                    </Link>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Admin ────────────────────────────────────────────────────────────────


// ── Email Templates Tab ──────────────────────────────────────────────────────

function bodyToParas(body) {
  if (!body) return [];
  var s = String(body);
  // Replace all literal backslash-n sequences with real newlines
  while (s.indexOf(String.fromCharCode(92) + 'n') !== -1) {
    s = s.replace(String.fromCharCode(92) + 'n', String.fromCharCode(10));
  }
  return s.split(String.fromCharCode(10)).filter(function(p) { return p.trim(); });
}

function previewSubstitute(str) {
  if (!str) return '';
  return str
    .replace(/\{product\}/g, 'Clearview')
    .replace(/\{client_name\}/g, 'Example Client Ltd')
    .replace(/\{username\}/g, 'test.user@example.com')
    .replace(/\{contact\}/g, '{configured contact email}')
    .replace(/\{ref\}/g, 'TKT-0042')
    .replace(/\{title\}/g, 'Wifi keeps dropping in the office')
    .replace(/\{type\}/g, 'Incident')
    .replace(/\{priority\}/g, 'High')
    .replace(/\{status\}/g, 'In Progress')
    .replace(/\{raised_by\}/g, 'jane@exampleclient.com')
    .replace(/\{comment_body\}/g, 'Thanks for reporting this \u2014 we\u2019ve escalated to our network team and will update you shortly.')
    .replace(/\{commenter\}/g, 'STG Support')
    .replace(/\{contact_name\}/g, 'Alex')
    .replace(/\{sender_name\}/g, 'The STG team')
    .replace(/\{grade\}/g, 'C')
    .replace(/\{report_date\}/g, '24 October 2026');
}

function AssessmentEmailToggle() {
  const [on, setOn]       = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]     = useState('');

  useEffect(() => {
    authFetch('/api/admin/assessment-config')
      .then(r => r.json())
      .then(d => { setOn(!!d.auto_email_clients); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  const toggle = async (next) => {
    setSaving(true); setMsg('');
    try {
      const res = await authFetch('/api/admin/assessment-config', {
        method: 'POST', body: JSON.stringify({ auto_email_clients: next }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Save failed');
      setOn(!!d.auto_email_clients);
      setMsg(next ? 'On — completed assessment reports will be emailed to the client.'
                  : 'Off — assessment reports are not emailed automatically.');
      setTimeout(() => setMsg(''), 5000);
    } catch (e) { setMsg('Error: ' + e.message); setTimeout(() => setMsg(''), 6000); }
    finally { setSaving(false); }
  };

  return (
    <div style={{border:'1px solid var(--border)',borderRadius:10,padding:'16px 18px',marginBottom:20,background:'var(--surface2)'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:16,flexWrap:'wrap'}}>
        <div>
          <div style={{fontSize:14,fontWeight:700,color:'var(--text)',marginBottom:3}}>Email External Cyber Security Assessments automatically</div>
          <div style={{fontSize:12,color:'var(--text-muted)',maxWidth:560}}>
            When an External Cyber Security Assessment finishes, email the report to the client's contact address, using the External Cyber Security Assessment template below. Clients only, never prospects. Off by default.
            IT and Cyber Security Reports and Website Analytics Reports are emailed on their schedules (the <a href="/schedules" style={{color:'var(--accent)'}}>Schedules</a> page), not by this setting.
          </div>
        </div>
        <label className="toggle-label" style={{whiteSpace:'nowrap',opacity:loaded?1:0.5}}>
          <input type="checkbox" checked={on} disabled={!loaded||saving}
                 onChange={e => toggle(e.target.checked)} />
          {saving ? <span className="spinner" /> : (on ? 'On' : 'Off')}
        </label>
      </div>
      {msg && <div className={msg.startsWith('Error')?'error-msg':'success-msg'} style={{marginTop:12}}>{msg}</div>}
    </div>
  );
}

const M365_LABEL = { fontSize:11, fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase',
  letterSpacing:'0.07em', display:'block', marginBottom:6 };

function BackupCard() {
  const [b, setB]           = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState(null);
  const flash = (text, ok = true) => { setMsg({ text, ok }); setTimeout(() => setMsg(null), 6000); };

  const load = useCallback(() => {
    authFetch('/api/admin/backup/config').then(r => r.json()).then(setB).catch(() => setB({}));
  }, []);
  useEffect(() => { load(); }, [load]);
  const running = b?.run?.state === 'running';
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [running, load]);

  const set = (k, v) => setB(c => ({ ...c, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const res = await authFetch('/api/admin/backup/config', { method: 'PUT', body: JSON.stringify({
        enabled: b.enabled, time: b.time, keep_daily: Number(b.keep_daily), keep_weekly: Number(b.keep_weekly) }) });
      const d = await res.json();
      flash(res.ok ? 'Saved' : (d.error || 'Save failed'), res.ok);
    } catch (e) { flash(e.message, false); }
    finally { setSaving(false); }
  };
  const runNow = async () => {
    try {
      const res = await authFetch('/api/admin/backup/run', { method: 'POST' });
      const d = await res.json();
      flash(d.message || d.error, res.ok);
      setTimeout(load, 1500);
    } catch (e) { flash(e.message, false); }
  };

  if (!b) return null;
  const rows = [...(b.clients || [])].sort((x, y) =>
    (x.ok === false ? 0 : x.at ? 1 : 2) - (y.ok === false ? 0 : y.at ? 1 : 2) || x.name.localeCompare(y.name));

  return (
    <div className="card admin-card">
      <div className="admin-card-title">Client backups</div>
      <div style={{fontSize:12,color:'var(--text-muted)',marginBottom:12,lineHeight:1.5}}>
        Each client is backed up nightly, encrypted, to its team (Clearview Backups) and to the backup site
        (Clients). The private key is only needed to restore, from the client's Settings tab.
      </div>
      {msg && <div className={msg.ok ? 'success-msg' : 'error-msg'} style={{marginBottom:12}}>{msg.text}</div>}
      {!b.public_key_ok && (
        <div className="error-msg" style={{marginBottom:12}}>
          {b.public_key_set ? 'STG_BACKUP_PUBLIC_KEY is not a valid age public key.' : 'STG_BACKUP_PUBLIC_KEY is not set in .env.'}
        </div>
      )}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:14,alignItems:'end'}}>
        <label style={{display:'flex',alignItems:'center',gap:8,fontWeight:600,cursor:'pointer'}}>
          <input type="checkbox" checked={!!b.enabled} onChange={e => set('enabled', e.target.checked)} style={{width:'auto'}} />
          Nightly backups on
        </label>
        <div>
          <label style={M365_LABEL}>Time (UK)</label>
          <input className="input-sm" type="time" value={b.time || ''} onChange={e => set('time', e.target.value)} style={{width:'100%'}} />
        </div>
        <div>
          <label style={M365_LABEL}>Nightly copies kept</label>
          <input className="input-sm" type="number" min={1} max={90} value={b.keep_daily ?? ''} onChange={e => set('keep_daily', e.target.value)} style={{width:'100%'}} />
        </div>
        <div>
          <label style={M365_LABEL}>Weekly copies kept</label>
          <input className="input-sm" type="number" min={0} max={52} value={b.keep_weekly ?? ''} onChange={e => set('keep_weekly', e.target.value)} style={{width:'100%'}} />
        </div>
      </div>
      <div style={{display:'flex',gap:8,flexWrap:'wrap',marginTop:14}}>
        <button className="btn btn-primary btn-sm" disabled={saving} onClick={save}>{saving ? <span className="spinner" /> : 'Save'}</button>
        <button className="btn btn-secondary btn-sm" disabled={running || !b.public_key_ok} onClick={runNow}>
          {running ? <><span className="spinner" /> Backing up {b.run.current ? b.run.current.replace(/_/g, ' ') : ''} ({b.run.done || 0} of {b.run.total || '?'})</> : 'Back up all now'}
        </button>
      </div>
      {b.run?.state === 'done' && (
        <div style={{fontSize:12,color: (b.run.failed || []).length ? 'var(--red)' : 'var(--text-muted)',marginTop:10}}>
          Last run {fmtTime(b.run.finished)}: {(b.run.total || 0) - (b.run.failed || []).length} of {b.run.total || 0} backed up
          {(b.run.failed || []).length ? `, failed: ${b.run.failed.map(s => s.replace(/_/g, ' ')).join(', ')}` : ''}
        </div>
      )}
      <div style={{marginTop:14,display:'flex',flexDirection:'column'}}>
        {rows.map((r, i) => (
          <div key={r.slug} style={{display:'flex',gap:10,alignItems:'flex-start',padding:'7px 0',borderTop: i ? '1px solid var(--border)' : 'none'}}>
            <span style={{paddingTop:5}}><StatusDot rag={r.ok === false ? 'red' : r.ok ? 'green' : 'grey'} /></span>
            <div style={{minWidth:0,flex:1}}>
              <div style={{fontSize:13,fontWeight:600,wordBreak:'break-word'}}>
                {r.name}{!r.team && <span style={{fontWeight:400,color:'var(--text-muted)'}}> · No team, backup site only</span>}
              </div>
              <div style={{fontSize:12,color: r.ok === false ? 'var(--red)' : 'var(--text-muted)',wordBreak:'break-word'}}>
                {r.at ? `${r.ok ? 'Backed up' : 'Failed'} ${fmtTime(r.at)}${r.size ? ` · ${fmtBytes(r.size)}` : ''}` : 'Not backed up yet'}
                {r.ok === false && r.error ? ` · ${r.error}` : ''}
                {r.ok === false && r.last_good ? ` · Last good ${fmtTime(r.last_good)}` : ''}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function M365Tab() {
  const [cfg, setCfg]         = useState(null);
  const [owners, setOwners]   = useState('');
  const [members, setMembers] = useState('');
  const [saving, setSaving]   = useState(false);
  const [testing, setTesting] = useState(false);
  const [test, setTest]       = useState(null);
  const [msg, setMsg]         = useState(null);

  const flash = (text, ok = true) => { setMsg({ text, ok }); setTimeout(() => setMsg(null), 6000); };

  const apply = (d) => {
    setCfg(d);
    setOwners((d.team_owners || []).join('\n'));
    setMembers((d.team_members || []).join('\n'));
  };

  const load = useCallback(() => {
    authFetch('/api/admin/m365/config').then(r => r.json()).then(apply).catch(() => setCfg({}));
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setCfg(c => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await authFetch('/api/admin/m365/config', { method: 'PUT', body: JSON.stringify({
        team_owners: owners, team_members: members,
        team_name_format: cfg.team_name_format, channel_name: cfg.channel_name,
        backup_site_url: cfg.backup_site_url,
      }) });
      const d = await res.json();
      if (res.ok) { apply({ ...cfg, ...d }); flash('Saved'); }
      else flash(d.error || 'Save failed', false);
    } catch (e) { flash(e.message, false); }
    finally { setSaving(false); }
  };

  const runTest = async () => {
    setTesting(true); setTest(null);
    try {
      const res = await authFetch('/api/admin/m365/test', { method: 'POST' });
      const d = await res.json();
      if (res.ok) setTest(d); else flash(d.error || 'Test failed', false);
    } catch (e) { flash(e.message, false); }
    finally { setTesting(false); }
  };

  if (!cfg) return <div style={{padding:48,textAlign:'center'}}><span className="spinner" /></div>;

  return (
    <div style={{display:'flex',flexDirection:'column',gap:16,maxWidth:760}}>
      {msg && <div className={msg.ok ? 'success-msg' : 'error-msg'}>{msg.text}</div>}

      {!cfg.configured && (
        <div className="error-msg">
          Not set up yet. Missing: {(cfg.missing || []).join(', ')}. Add them to .env (and graph.key to
          portal/data/graph), then recreate portal-api.
        </div>
      )}

      <div className="card admin-card">
        <div className="admin-card-title">Client teams</div>
        <div style={{fontSize:12,color:'var(--text-muted)',marginBottom:14,lineHeight:1.5}}>
          New client teams are private, with one channel for the client's files. The first owner creates
          the team; everyone else is added once it exists.
        </div>

        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))',gap:14}}>
          <div>
            <label style={M365_LABEL}>Team owners</label>
            <textarea className="input-sm" rows={4} value={owners} onChange={e => setOwners(e.target.value)}
              placeholder="One email address per line" style={{width:'100%',fontFamily:'inherit'}} />
          </div>
          <div>
            <label style={M365_LABEL}>Team members (optional)</label>
            <textarea className="input-sm" rows={4} value={members} onChange={e => setMembers(e.target.value)}
              placeholder="Day-to-day accounts, one per line" style={{width:'100%',fontFamily:'inherit'}} />
          </div>
          <div>
            <label style={M365_LABEL}>Team name format</label>
            <input className="input-sm" value={cfg.team_name_format || ''} style={{width:'100%'}}
              onChange={e => set('team_name_format', e.target.value)} />
            <div style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>
              {'{name}'} is the client name, e.g. {(cfg.team_name_format || '').replace('{name}', 'JK Tyres')}
            </div>
          </div>
          <div>
            <label style={M365_LABEL}>Channel name</label>
            <input className="input-sm" value={cfg.channel_name || ''} style={{width:'100%'}}
              onChange={e => set('channel_name', e.target.value)} />
          </div>
        </div>
      </div>

      <div className="card admin-card">
        <div className="admin-card-title">Platform backup</div>
        <div style={{fontSize:12,color:'var(--text-muted)',marginBottom:12,lineHeight:1.5}}>
          The SharePoint site the whole-platform backup will go to. Keep it private to STG admins and
          leave it out of Active Backup for M365.
        </div>
        <label style={M365_LABEL}>Backup site address</label>
        <input className="input-sm" type="url" value={cfg.backup_site_url || ''} style={{width:'100%'}}
          placeholder="https://<tenant>.sharepoint.com/sites/<name>"
          onChange={e => set('backup_site_url', e.target.value)} />
      </div>

      <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
        <button className="btn btn-primary" disabled={saving} onClick={save}>
          {saving ? <span className="spinner" /> : 'Save'}
        </button>
        <button className="btn btn-secondary" disabled={testing || !cfg.configured} onClick={runTest}>
          {testing ? <><span className="spinner" /> Testing...</> : 'Test connection'}
        </button>
      </div>

      <BackupCard />

      {test && (
        <div className="card admin-card">
          <div className="admin-card-title">
            Connection test {test.ok ? 'passed' : 'found problems'}
            <span style={{fontWeight:400,fontSize:12,color:'var(--text-muted)',marginLeft:8}}>Updated {fmtTime(test.tested)}</span>
          </div>
          {(test.checks || []).map((c, i) => (
            <div key={i} style={{display:'flex',gap:10,alignItems:'flex-start',padding:'6px 0',borderTop: i ? '1px solid var(--border)' : 'none'}}>
              <span style={{paddingTop:5}}><StatusDot rag={c.ok ? 'green' : 'red'} /></span>
              <div style={{minWidth:0}}>
                <div style={{fontSize:13,fontWeight:600,wordBreak:'break-word'}}>{c.name}</div>
                <div style={{fontSize:12,color:'var(--text-muted)',wordBreak:'break-word'}}>{c.detail}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EmailTemplatesTab() {
  const [templates,    setTemplates]    = useState({});
  const [loading,      setLoading]      = useState(true);
  const [selected,     setSelected]     = useState('');
  const [editing,      setEditing]      = useState({});
  const [saving,       setSaving]       = useState(false);
  const [testEmail,    setTestEmail]    = useState('');
  const [sending,      setSending]      = useState(false);
  const [flash,        setFlash]        = useState(null);
  // Email header tagline and footer line come from the server settings
  // (EMAIL_HEADER_TAGLINE / EMAIL_FOOTER_LINE) so the preview matches what is sent
  const [branding,     setBranding]     = useState({ header: 'Technology Management Portal', footer: 'Swanswater Technology Group (STG) Limited' });

  useEffect(() => {
    authFetch('/api/admin/email-branding')
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && d.header !== undefined) setBranding({ header: d.header, footer: d.footer }); })
      .catch(() => {});
  }, []);

  const showFlash = (msg, ok=true) => { setFlash({ msg, ok }); setTimeout(() => setFlash(null), 5000); };

  useEffect(() => {
    authFetch('/api/admin/email-templates')
      .then(r => r.json())
      .then(d => {
        setTemplates(d);
        const first = Object.keys(d)[0] || '';
        setSelected(first);
        if (first) setEditing(d[first]);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const selectTemplate = (key) => {
    setSelected(key);
    setEditing(templates[key] || {});
  };

  const updateField = (field, value) => {
    setEditing(e => ({ ...e, [field]: value }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`/api/admin/email-templates/${selected}`, {
        method: 'POST', body: JSON.stringify(editing),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Save failed');
      setTemplates(t => ({ ...t, [selected]: { ...t[selected], ...editing, customised: true } }));
      showFlash('Template saved');
    } catch (e) {
      showFlash(e.message, false);
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm('Reset this template to the default? Your customisations will be lost.')) return;
    setSaving(true);
    try {
      const res = await authFetch(`/api/admin/email-templates/${selected}/reset`, { method: 'POST' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Reset failed');
      // Reload templates
      const r2 = await authFetch('/api/admin/email-templates');
      const all = await r2.json();
      setTemplates(all);
      setEditing(all[selected] || {});
      showFlash('Template reset to default');
    } catch (e) {
      showFlash(e.message, false);
    } finally { setSaving(false); }
  };

  const makeDefault = async () => {
    if (!window.confirm('Make this the default for everyone? This replaces the built-in default with the current wording — it will no longer show as customised, and this cannot be undone from here.')) return;
    setSaving(true);
    try {
      // make-default promotes whatever is currently saved server-side, so
      // persist the editor's current state first — otherwise unsaved
      // changes in the form would silently not become the new default.
      const saveRes = await authFetch(`/api/admin/email-templates/${selected}`, {
        method: 'POST', body: JSON.stringify(editing),
      });
      if (!saveRes.ok) { const d = await saveRes.json(); throw new Error(d.error || 'Save failed'); }

      const res = await authFetch(`/api/admin/email-templates/${selected}/make-default`, { method: 'POST' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Failed to set as default');

      const r2 = await authFetch('/api/admin/email-templates');
      const all = await r2.json();
      setTemplates(all);
      setEditing(all[selected] || {});
      showFlash('Template is now the default');
    } catch (e) {
      showFlash(e.message, false);
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testEmail || !testEmail.includes('@')) {
      showFlash('Enter a valid email address', false);
      return;
    }
    setSending(true);
    try {
      const res = await authFetch('/api/admin/email-templates/test', {
        method: 'POST',
        body: JSON.stringify({ template: selected, email: testEmail }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Send failed');
      showFlash(d.message);
    } catch (e) {
      showFlash(e.message, false);
    } finally { setSending(false); }
  };

  if (loading) return <div style={{padding:48,textAlign:'center'}}><span className="spinner" /></div>;

  const tpl = editing || {};
  const placeholderList = tpl.placeholders || [];

  return (
    <div>
      <AssessmentEmailToggle />
      <div style={{fontSize:13,color:'var(--text-muted)',marginBottom:20}}>
        Customise the wording of portal emails. Changes are saved and used immediately.
      </div>

      {flash && <div className={flash.ok ? 'success-msg' : 'error-msg'} style={{marginBottom:16}}>{flash.msg}</div>}

      {/* Template selector */}
      <div style={{marginBottom:20}}>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          {Object.entries(templates).map(([key, tpl]) => (
            <button className="btn btn-secondary btn-sm" aria-pressed={selected === key} key={key} onClick={() => selectTemplate(key)}>
              {tpl.label}
              {tpl.customised && <span style={{marginLeft:6,fontSize:9,color: selected === key ? '#000' : 'var(--accent)'}}>●</span>}
            </button>
          ))}
        </div>
      </div>

      {selected && (
        <div className="tpl-grid">
          {/* Editor */}
          <div>
            <div style={{fontSize:11,fontWeight:600,letterSpacing:'0.07em',textTransform:'uppercase',color:'var(--text-muted)',marginBottom:12}}>Edit Template</div>

            <div style={{marginBottom:14}}>
              <label style={{fontSize:12,fontWeight:600,display:'block',marginBottom:4}}>Subject Line</label>
              <input className="input-sm" value={tpl.subject || ''} onChange={e => updateField('subject', e.target.value)}
                style={{ width:'100%' }} />
            </div>

            <div style={{marginBottom:14}}>
              <label style={{fontSize:12,fontWeight:600,display:'block',marginBottom:4}}>Heading</label>
              <input className="input-sm" value={tpl.heading || ''} onChange={e => updateField('heading', e.target.value)}
                style={{ width:'100%' }} />
            </div>

            <div style={{marginBottom:14}}>
              <label style={{fontSize:12,fontWeight:600,display:'block',marginBottom:4}}>Body Text</label>
              <textarea className="input-sm" value={(tpl.body || '').split('\\n').join('\n')} onChange={e => updateField('body', e.target.value.split('\n').join('\\n'))}
                rows={6} style={{ width:'100%', resize:'vertical' }} />
              <div style={{fontSize:10,color:'var(--text-muted)',marginTop:4}}>Use blank lines to separate paragraphs</div>
            </div>

            <div style={{marginBottom:14}}>
              <label style={{fontSize:12,fontWeight:600,display:'block',marginBottom:4}}>Contact Line</label>
              <input className="input-sm" value={tpl.contact_line || ''} onChange={e => updateField('contact_line', e.target.value)}
                style={{ width:'100%' }} />
            </div>

            {placeholderList.length > 0 && (
              <div style={{marginBottom:14,padding:'10px 14px',background:'var(--surface2)',borderRadius:8,border:'1px solid var(--border)'}}>
                <div style={{fontSize:10,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:6}}>Available Placeholders</div>
                <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                  {placeholderList.map(p => (
                    <span key={p} style={{fontSize:11,padding:'2px 8px',borderRadius:4,background:'var(--card-bg)',border:'1px solid var(--border)',fontFamily:'var(--font-mono)',color:'var(--accent)'}}>
                      {'{' + p + '}'}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div style={{display:'flex',gap:8,marginBottom:20,flexWrap:'wrap'}}>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? <span className="spinner" /> : 'Save Template'}
              </button>
              <button className="btn btn-secondary" onClick={makeDefault} disabled={saving}>Make Default</button>
              <button className="btn btn-secondary" onClick={reset} disabled={saving}>Reset to Default</button>
            </div>

            {/* Send test */}
            <div style={{borderTop:'1px solid var(--border)',paddingTop:16}}>
              <div style={{fontSize:11,fontWeight:600,letterSpacing:'0.07em',textTransform:'uppercase',color:'var(--text-muted)',marginBottom:10}}>Send Test Email</div>
              <div style={{display:'flex',gap:8}}>
                <input className="input-sm" type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)}
                  placeholder="recipient@example.com"
                  style={{ flex:1 }} />
                <button className="btn btn-primary" onClick={sendTest} disabled={sending || !testEmail}>
                  {sending ? <span className="spinner" /> : 'Send Test'}
                </button>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div>
            <div style={{fontSize:11,fontWeight:600,letterSpacing:'0.07em',textTransform:'uppercase',color:'var(--text-muted)',marginBottom:12}}>Preview</div>
            <div style={{border:'1px solid var(--border)',borderRadius:12,overflow:'hidden',background:'#f0f0f5'}}>
              {/* Header */}
              <div style={{background:'#1d1d1f',padding:'20px 24px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <span style={{fontFamily:'monospace',fontSize:16,fontWeight:700,color:'#00d4ff',letterSpacing:'0.2em'}}>STG</span>
                <span style={{fontSize:12,color:'#86868b'}}>{branding.header}</span>
              </div>
              {/* Body */}
              <div style={{background:'#fff',padding:'28px 24px'}}>
                <h2 style={{margin:'0 0 10px',fontSize:18,fontWeight:800,color:'#1d1d1f'}}>
                  {previewSubstitute(tpl.heading || '')}
                </h2>
                {bodyToParas(tpl.body).map((para, i) => (
                  <p key={i} style={{margin:'0 0 12px',fontSize:13,color:'#555',lineHeight:1.6}}>
                    {previewSubstitute(para)}
                  </p>
                ))}
                {selected === 'welcome' && (
                  <div style={{background:'#f5f5f7',borderRadius:8,padding:'14px 18px',margin:'0 0 16px'}}>
                    <div style={{fontSize:10,fontWeight:600,letterSpacing:'0.07em',textTransform:'uppercase',color:'#86868b',marginBottom:8}}>Your login details</div>
                    <div style={{fontSize:12,color:'#1d1d1f'}}><span style={{color:'#86868b',display:'inline-block',width:80}}>Email</span> <strong>test.user@example.com</strong></div>
                    <div style={{fontSize:12,color:'#1d1d1f',marginTop:4}}><span style={{color:'#86868b',display:'inline-block',width:80}}>Password</span> <strong>TempPass123!</strong></div>
                  </div>
                )}
                {selected === 'otp' && (
                  <div style={{background:'#f5f5f7',borderRadius:10,padding:'20px',textAlign:'center',margin:'0 0 16px'}}>
                    <div style={{fontFamily:'monospace',fontSize:32,fontWeight:700,color:'#1d1d1f',letterSpacing:'0.3em'}}>847293</div>
                  </div>
                )}
                {selected === 'admin_registration' && (
                  <div style={{background:'#f5f5f7',borderRadius:8,padding:'14px 18px',margin:'0 0 16px'}}>
                    <div style={{fontSize:12,color:'#1d1d1f'}}><span style={{color:'#86868b',display:'inline-block',width:100}}>Email</span> <strong>test.user@example.com</strong></div>
                    <div style={{fontSize:12,color:'#1d1d1f',marginTop:4}}><span style={{color:'#86868b',display:'inline-block',width:100}}>Company</span> <strong>Example Client Ltd</strong></div>
                    <div style={{fontSize:12,color:'#1d1d1f',marginTop:4}}><span style={{color:'#86868b',display:'inline-block',width:100}}>Slug</span> <span style={{fontFamily:'monospace'}}>Example_Client</span></div>
                  </div>
                )}
                {selected === 'ticket_created_staff' && (
                  <div style={{background:'#f5f5f7',borderRadius:8,padding:'14px 18px',margin:'0 0 16px'}}>
                    <div style={{fontSize:12,color:'#1d1d1f'}}><span style={{color:'#86868b',display:'inline-block',width:100}}>Ref</span> <strong style={{fontFamily:'monospace'}}>TKT-0042</strong></div>
                    <div style={{fontSize:12,color:'#1d1d1f',marginTop:4}}><span style={{color:'#86868b',display:'inline-block',width:100}}>Client</span> <strong>Example Client Ltd</strong></div>
                    <div style={{fontSize:12,color:'#1d1d1f',marginTop:4}}><span style={{color:'#86868b',display:'inline-block',width:100}}>Type</span> Incident</div>
                    <div style={{fontSize:12,color:'#1d1d1f',marginTop:4}}><span style={{color:'#86868b',display:'inline-block',width:100}}>Priority</span> High</div>
                    <div style={{fontSize:12,color:'#1d1d1f',marginTop:4}}><span style={{color:'#86868b',display:'inline-block',width:100}}>Raised by</span> jane@exampleclient.com</div>
                    <div style={{fontSize:12,color:'#1d1d1f',marginTop:4}}><span style={{color:'#86868b',display:'inline-block',width:100}}>Title</span> Wifi keeps dropping in the office</div>
                  </div>
                )}
                {['welcome','approval','admin_registration'].includes(selected) && (
                  <div style={{margin:'0 0 16px'}}>
                    <span style={{display:'inline-block',background:'#00d4ff',color:'#000',fontSize:13,fontWeight:600,padding:'10px 20px',borderRadius:8}}>Access the Portal</span>
                  </div>
                )}
                {['ticket_created_client','ticket_status_changed_client','ticket_comment_client','ticket_created_staff'].includes(selected) && (
                  <div style={{margin:'0 0 16px'}}>
                    <span style={{display:'inline-block',background:'#00d4ff',color:'#000',fontSize:13,fontWeight:600,padding:'10px 20px',borderRadius:8}}>View in Portal →</span>
                  </div>
                )}
                {tpl.contact_line && (
                  <p style={{margin:'20px 0 0',fontSize:11,color:'#aeaeb2'}}>
                    {previewSubstitute(tpl.contact_line || '')}
                  </p>
                )}
                {selected === 'prospect' && (
                  <p style={{margin:'20px 0 0',paddingTop:12,borderTop:'1px solid #e5e5ea',fontSize:10,color:'#aeaeb2',lineHeight:1.6}}>
                    You are receiving this because Example Client Ltd is listed on Companies House. If you would rather not hear from us, <span style={{textDecoration:'underline',color:'#86868b'}}>unsubscribe here</span> or reply with "unsubscribe".
                    <span style={{display:'block',marginTop:6,fontStyle:'italic'}}>Added automatically to every outreach email. Not editable here.</span>
                  </p>
                )}
              </div>
              {/* Footer */}
              <div style={{background:'#f5f5f7',padding:'12px 24px',borderTop:'1px solid #e5e5ea',textAlign:'center'}}>
                <span style={{fontSize:10,color:'#aeaeb2'}}>{branding.footer}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export default function Admin() {
  // v2.21: ?tab=alerts (the dashboard's Alerts item) opens that tab
  const [tab,      setTab]      = useState(() => {
    try { const t = new URLSearchParams(window.location.search).get('tab'); return ['system', 'integrations', 'jobs', 'alerts', 'm365', 'templates'].includes(t) ? t : 'system'; } catch (e) { return 'system'; }
  });
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [logs,     setLogs]     = useState({});
  const [acting,   setActing]   = useState({});
  const [messages, setMessages] = useState([]);
  const [failedScans, setFailedScans] = useState([]);
  const [buildLog, setBuildLog] = useState([]);
  const [building, setBuilding] = useState(false);
  const [buildDone, setBuildDone] = useState(false);
  const [apiBuildLog, setApiBuildLog] = useState([]);
  const [apiBuildDone, setApiBuildDone] = useState(false);
  const [buildingApi, setBuildingApi] = useState(false);
  const apiBuildLogRef = useRef(null);
  const buildLogRef = useRef(null);

  const flash = (msg, ok = true) => {
    const id = Date.now();
    setMessages(m => [...m, {id, msg, ok}]);
    setTimeout(() => setMessages(m => m.filter(x => x.id !== id)), 5000);
  };

  const fetchData = useCallback(() => {
    authFetch('/api/admin/system').then(r => r.json()).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const [versions, setVersions] = useState(null);
  const fetchVersions = useCallback(() => {
    authFetch('/api/admin/versions').then(r => r.ok ? r.json() : null).then(d => { if (d) setVersions(d); }).catch(() => {});
  }, []);
  useEffect(() => { fetchVersions(); const i = setInterval(fetchVersions, 60000); return () => clearInterval(i); }, [fetchVersions]);

  const [fleet, setFleet] = useState(null);
  const [maint, setMaint] = useState(null);
  const [jobs, setJobs]   = useState(null);
  const [jobsLoading, setJobsLoading] = useState(false);
  const fetchFleet = useCallback(() => {
    authFetch('/api/admin/edge-fleet').then(r => r.ok ? r.json() : null).then(d => { if (d) setFleet(d); }).catch(() => {});
  }, []);
  const fetchMaint = useCallback((refresh = false) => {
    return authFetch(`/api/admin/maintenance/info${refresh ? '?refresh=1' : ''}`)
      .then(r => r.ok ? r.json() : null).then(d => { if (d) setMaint(d); }).catch(() => {});
  }, []);
  const fetchJobs = useCallback(() => {
    setJobsLoading(true);
    authFetch('/api/admin/jobs').then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setJobs(d); }).catch(() => {}).finally(() => setJobsLoading(false));
  }, []);
  useEffect(() => {
    fetchFleet(); fetchMaint(); fetchJobs();
    const i = setInterval(() => { fetchFleet(); fetchJobs(); }, 60000);
    const k = setInterval(() => fetchMaint(), 300000);
    return () => { clearInterval(i); clearInterval(k); };
  }, [fetchFleet, fetchMaint, fetchJobs]);

  const restartRunning = !!data?.restart_all?.running;
  useEffect(() => {
    fetchData();
    const i = setInterval(fetchData, restartRunning ? 4000 : 15000);
    return () => clearInterval(i);
  }, [fetchData, restartRunning]);
  useEffect(() => { if (buildLogRef.current) buildLogRef.current.scrollTop = buildLogRef.current.scrollHeight; }, [buildLog]);
  useEffect(() => { if (apiBuildLogRef.current) apiBuildLogRef.current.scrollTop = apiBuildLogRef.current.scrollHeight; }, [apiBuildLog]);

  const act = async (key, fn) => {
    setActing(a => ({...a, [key]: true}));
    try { await fn(); } finally { setActing(a => ({...a, [key]: false})); setTimeout(fetchData, 2000); }
  };

  const restartContainer = (c) => act(c.name, async () => {
    const verb = c.status === 'not deployed' ? 'Start' : 'Restart';
    const prompt = c.restart_warning
      ? `${verb} ${c.label}?\n\n${c.restart_warning}`
      : `${verb} ${c.label}?`;
    if (!window.confirm(prompt)) return;
    const res = await authFetch(`/api/admin/containers/${c.name}/restart`, { method: 'POST' });
    const d = await res.json(); flash(d.message || d.error, res.ok);
  });

  const restartAll = () => act('all', async () => {
    const targets = (data?.containers || []).filter(c => c.in_core_restart && c.in_compose && c.restart_allowed);
    const names = targets.map(c => c.label).join(', ');
    const msg = `Restart ${targets.length} core services?\n\n${names}\n\n`
      + 'Redis restarts first and Portal API last (via the watchdog, within 60 seconds). '
      + 'The Portal Tunnel is not restarted, so this session stays connected, but the portal '
      + 'may be unresponsive for up to a minute while the API comes back.';
    if (!window.confirm(msg)) return;
    const res = await authFetch('/api/admin/maintenance/restart-all', { method: 'POST' });
    const d = await res.json(); flash(d.message || d.error, res.ok);
  });

  const [runningAllChecks, setRunningAllChecks] = useState(false);
  const [runningAction1, setRunningAction1]     = useState(false);

  const runAllChecks = async () => {
    if (!window.confirm('Run all cyber hub checks now for all clients? This runs in the background and may take 10-15 minutes.')) return;
    setRunningAllChecks(true);
    try {
      const res = await authFetch('/api/admin/run-all-checks', { method: 'POST' });
      const d = await res.json();
      flash(d.message || d.error, res.ok);
    } catch (err) {
      flash('Failed to trigger checks', false);
    } finally {
      setTimeout(() => setRunningAllChecks(false), 3000);
    }
  };

  const runAction1 = async () => {
    if (!window.confirm('Run Action1 sync now for all clients? Results will update within a few minutes.')) return;
    setRunningAction1(true);
    try {
      const res = await authFetch('/api/admin/run-action1', { method: 'POST' });
      const d = await res.json();
      flash(d.message || d.error, res.ok);
    } catch (err) {
      flash('Failed to trigger Action1 sync', false);
    } finally {
      setTimeout(() => setRunningAction1(false), 3000);
    }
  };

  const pruneImages = () => act('prune', async () => {
    if (!window.confirm('Remove all dangling Docker images on this NAS? These are untagged images left behind by rebuilds and pulls, across every stack. Images used by containers are not touched.')) return;
    const res = await authFetch('/api/admin/maintenance/prune-images', { method: 'POST' });
    const d = await res.json(); flash(d.message || d.error, res.ok); fetchMaint();
  });

  const trimWatchdogLog = () => act('trim', async () => {
    if (!window.confirm('Trim the watchdog log to its last 2000 lines?')) return;
    const res = await authFetch('/api/admin/maintenance/trim-watchdog-log', { method: 'POST' });
    const d = await res.json(); flash(d.message || d.error, res.ok); fetchMaint(); fetchVersions();
  });

  const recalcStorage = () => act('recalc', async () => { await fetchMaint(true); });

  const clearLogs = () => act('clear', async () => {
    const res = await authFetch('/api/admin/maintenance/clear-logs', { method: 'POST' });
    const d = await res.json(); flash(d.message || d.error, res.ok); fetchData();
  });

  const fetchFailedScans = useCallback(() => {
    authFetch('/api/admin/failed-scans').then(r => r.json()).then(d => setFailedScans(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  useEffect(() => { fetchFailedScans(); const i = setInterval(fetchFailedScans, 15000); return () => clearInterval(i); }, [fetchFailedScans]);

  const clearFailedScan = (slug) => act(`clear-${slug}`, async () => {
    const res = await authFetch(`/api/admin/clear-failed-scan/${slug}`, { method: 'POST' });
    const d = await res.json(); flash(d.message || d.error, res.ok); fetchFailedScans();
  });

  const fetchLogs = async (name) => {
    if (logs[name]) { setLogs(l => ({...l, [name]: null})); return; }
    const res = await authFetch(`/api/admin/containers/${name}/logs?lines=50`);
    const d = await res.json(); setLogs(l => ({...l, [name]: d.logs || d.error}));
  };

  const rebuildApi = async () => {
    if (!window.confirm('Rebuild the API? This takes 1-2 minutes.')) return;
    setBuildingApi(true); setApiBuildDone(false); setApiBuildLog([]);
    try {
      const sseToken = await getSseToken();
      const es = new EventSource(`/api/admin/rebuild-api?token=${sseToken}`);
      es.onmessage = (e) => {
        if (e.data === '[DONE]') { es.close(); setBuildingApi(false); setApiBuildDone(true); flash('API rebuilt — will restart within 60 seconds', true); }
        else if (e.data.startsWith('[ERROR]')) { es.close(); setBuildingApi(false); flash('API build failed — check log below', false); setApiBuildLog(prev => [...prev, e.data]); }
        else { setApiBuildLog(prev => [...prev, e.data].slice(-100)); }
      };
      es.onerror = () => { es.close(); setBuildingApi(false); flash('Build stream disconnected', false); };
    } catch (err) { setBuildingApi(false); flash(err.message, false); }
  };

  const syncFrontend = async () => {
    const res = await authFetch('/api/admin/sync-frontend', { method: 'POST' });
    const d = await res.json(); flash(d.message || d.error, res.ok);
  };

  const rebuildFrontend = async () => {
    if (!window.confirm('Rebuild the frontend? This takes 2-3 minutes.')) return;
    setBuilding(true); setBuildDone(false); setBuildLog([]);
    try {
      const sseToken = await getSseToken();
      const es = new EventSource(`/api/admin/rebuild-frontend?token=${sseToken}`);
      es.onmessage = (e) => {
        if (e.data === '[DONE]') { es.close(); setBuilding(false); setBuildDone(true); flash('Frontend rebuilt successfully', true); }
        else if (e.data.startsWith('[ERROR]')) { es.close(); setBuilding(false); flash('Build failed — check log below', false); setBuildLog(prev => [...prev, e.data]); }
        else { setBuildLog(prev => [...prev, e.data].slice(-100)); }
      };
      es.onerror = () => { es.close(); setBuilding(false); flash('Build stream disconnected', false); };
    } catch (err) { setBuilding(false); flash(err.message, false); }
  };

  const TABS = [
    { id: 'system', label: 'System' },
    { id: 'integrations', label: 'Integrations' },
    { id: 'jobs', label: (jobs?.summary?.red || 0) + (jobs?.summary?.amber || 0) > 0
        ? `Jobs (${(jobs.summary.red || 0) + (jobs.summary.amber || 0)})` : 'Jobs' },
    { id: 'alerts', label: 'Health Alerts' },
    { id: 'm365', label: 'Microsoft 365' },
    { id: 'templates', label: 'Email Templates' },
  ];

  return (
    <div>
      <div className="page-header" style={{display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:12}}>
        <div>
          <div className="page-title">System Admin</div>
          <div className="page-subtitle">Service management and maintenance</div>
        </div>
        {tab === 'system' && (
          <button className="btn btn-danger" onClick={restartAll} disabled={acting['all'] || restartRunning || !data}>
            {acting['all'] || restartRunning ? <><span className="spinner" /> Restarting...</> : '↺ Restart Core Services'}
          </button>
        )}
      </div>

      {tab === 'system' && (
        <div className="admin-toolbar admin-toolbar-row">
            <div className="admin-toolbar-group">
            <button className="btn btn-secondary" onClick={() => { fetchData(); fetchVersions(); fetchFleet(); fetchMaint(); fetchJobs(); }}>↻ Refresh</button>
            </div>
            <div className="admin-toolbar-group" aria-label="Deploy">
            <button className="btn btn-secondary" onClick={rebuildApi} disabled={buildingApi}>
              {buildingApi ? <><span className="spinner" /> Building API...</> : '⟳ Rebuild API'}
            </button>
            <button className="btn btn-secondary" onClick={rebuildFrontend} disabled={building}>
              {building ? <><span className="spinner" /> Building...</> : '⟳ Rebuild Frontend'}
            </button>
            <button className="btn btn-secondary" onClick={syncFrontend} disabled={acting['sync']}>
              {acting['sync'] ? <span className="spinner" /> : '⇪ Sync Frontend'}
            </button>
            </div>
            <div className="admin-toolbar-group" aria-label="Run">
            <button className="btn btn-secondary" onClick={runAllChecks} disabled={runningAllChecks}>
              {runningAllChecks ? <><span className="spinner" /> Running...</> : '▶ Run All Checks'}
            </button>
            <button className="btn btn-secondary" onClick={runAction1} disabled={runningAction1}>
              {runningAction1 ? <><span className="spinner" /> Syncing...</> : '▶ Run Action1'}
            </button>
            </div>
        </div>
      )}

      {messages.map(m => <div key={m.id} className={m.ok ? 'success-msg' : 'error-msg'} style={{marginBottom:12}}>{m.msg}</div>)}

      {/* Tab bar */}
      <div className="admin-tabbar" style={{display:'flex',gap:2,borderBottom:'1px solid var(--border)',marginBottom:20,overflowX:'auto',overflowY:'hidden',scrollbarWidth:'none',WebkitOverflowScrolling:'touch'}}>
        {TABS.map(t => (
          <button className="tab-btn" aria-selected={tab === t.id} style={{ flexShrink: 0 }} key={t.id} onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {tab === 'system' && (
        <SystemTab
          data={data} loading={loading} logs={logs} acting={acting} messages={messages}
          failedScans={failedScans} buildLog={buildLog} building={building} buildDone={buildDone}
          apiBuildLog={apiBuildLog} apiBuildDone={apiBuildDone} buildingApi={buildingApi}
          buildLogRef={buildLogRef} apiBuildLogRef={apiBuildLogRef}
          onFetchLogs={fetchLogs} onRestartContainer={restartContainer} onRestartAll={restartAll}
          onClearLogs={clearLogs} onClearFailedScan={clearFailedScan}
          onRebuildApi={rebuildApi} onRebuildFrontend={rebuildFrontend} onSyncFrontend={syncFrontend}
          versions={versions} fleet={fleet} maint={maint}
          onPrune={pruneImages} onTrim={trimWatchdogLog} onRecalc={recalcStorage}
        />
      )}
      {tab === 'jobs' && <JobsTab jobs={jobs} loading={jobsLoading} onRefresh={fetchJobs} />}
      {tab === 'integrations' && <IntegrationsTab />}
      {tab === 'alerts' && <AlertsTab />}
      {tab === 'm365' && <M365Tab />}
      {tab === 'templates' && <EmailTemplatesTab />}
    </div>
  );
}